// Card — a surface. Compose with Card.Header / Card.Body / Card.Footer, or
// pass `title`/`actions` for the common header shape. Token-driven, themes
// with brand + dark, and turns to frosted glass inside <AuroraPage>.
//
//   <Card title="Active Placements" actions={<Badge tone="good">12</Badge>}>
//     …table…
//   </Card>
//
//   <Card interactive onClick={open}>
//     <Card.Body>…</Card.Body>
//   </Card>

import { HTMLAttributes, ReactNode } from 'react';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Convenience header title (left side). */
  title?: ReactNode;
  /** Convenience header actions (right side). */
  actions?: ReactNode;
  /** Hover-lift + pointer. Pair with onClick. */
  interactive?: boolean;
  /** Drop the shadow (flush inside another surface). */
  flat?: boolean;
  /** Pad the body when using plain children (no Card.Body). Default true. */
  padded?: boolean;
  children?: ReactNode;
}

function Card({ title, actions, interactive, flat, padded = true, className, children, ...rest }: CardProps) {
  const cls = [
    'ui-card',
    interactive ? 'ui-card--interactive' : '',
    flat ? 'ui-card--flat' : '',
    className ?? '',
  ].filter(Boolean).join(' ');

  const hasHeader = title != null || actions != null;
  return (
    <div className={cls} {...rest}>
      {hasHeader && (
        <div className="ui-card__header">
          {title != null ? <div className="ui-card__title">{title}</div> : <span />}
          {actions != null ? <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>{actions}</div> : null}
        </div>
      )}
      {padded ? <div className="ui-card__body">{children}</div> : children}
    </div>
  );
}

Card.Header = function CardHeader({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={['ui-card__header', className].filter(Boolean).join(' ')} {...rest}>{children}</div>;
};
Card.Title = function CardTitle({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={['ui-card__title', className].filter(Boolean).join(' ')} {...rest}>{children}</div>;
};
Card.Body = function CardBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={['ui-card__body', className].filter(Boolean).join(' ')} {...rest}>{children}</div>;
};
Card.Footer = function CardFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={['ui-card__footer', className].filter(Boolean).join(' ')} {...rest}>{children}</div>;
};

export default Card;
